import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import {
  AppButton,
  AppDivider,
  AppImage,
  AppParagraph,
  AppFlexContainer,
  AppFlexItem,
} from 'client/ui-building-blocks'

import {
  useModalDialogContext,
  useAppContext,
  usePage,
  usePageStateContext,
} from 'client/hooks'

import { Filter, ApiUrlPath, READ_TOP_QUESTIONS_MAX } from 'cat-shared/utils'
import { QuestionsList } from 'client/components'
import { displaySnackBar, getAssetUrl } from 'client/services'
import { useGetStats, useGetTopQuestions } from 'client/api'

import Stats from './Stats/stats'
import UsersList from './UsersList/users-list'

const API_PATH = process.env.API_PATH

const HomePage = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { authUser } = useAppContext()
  const { setBrowseQuestionsPageState } = usePageStateContext()
  const { showQuestionModal } = useModalDialogContext()
  const { navigateTo } = usePage()

  const stats = useGetStats()

  const topQuestions = useGetTopQuestions({
    onError: err => {
      if (err?.msg) {
        displaySnackBar(err.msg)
      }
    },
  })

  useEffect(() => {
    if (showQuestionModal) {
      queryClient.cancelQueries([API_PATH + ApiUrlPath.GetQuestions, Filter.Top])
    }
  }, [showQuestionModal, queryClient])

  return useMemo(() => {
    const onStartAnsweringClick = () => {
      navigateTo({ path: '/random' })
    }

    const onAboutClick = () => {
      navigateTo({ path: '/about' })
    }

    return (
      <>
        <AppFlexContainer justifyContent="space-around" columnGap="50px">
          <AppFlexItem>
            <AppImage
              src={getAssetUrl('cat-bot-small.jpg')}
              width="250px"
              height="250px"
              rounded
              alt="logo"
            />
            <AppDivider />
          </AppFlexItem>
          <AppFlexItem flexGrow={1}>
            <AppFlexContainer flexFlow="column" alignItems="center">
              <AppFlexItem textAlign="center">
                <AppParagraph
                  text={t('homePage.intro.header')}
                  size="xxlarge"
                  isHeader
                  marginBottom="xxsmall"
                />
              </AppFlexItem>
              <AppFlexItem textAlign="center">
                <AppParagraph
                  text={t('homePage.intro.subheader1')}
                  color="secondary"
                  size="large"
                />
              </AppFlexItem>
              <AppFlexItem textAlign="center">
                <AppParagraph
                  text={t('homePage.intro.subheader2')}
                  color="secondary"
                  size="large"
                  marginBottom
                />
              </AppFlexItem>
              <AppButton
                variant="success"
                text={t('homePage.startAnswering.button')}
                onClick={onStartAnsweringClick}
              />
            </AppFlexContainer>
            <AppDivider border size="large" />
            <AppFlexContainer flexFlow="column" alignItems="center">
              <Stats stats={stats.data} />
              <AppDivider size="small" />
              <AppButton
                variant="outlined"
                text={t('homePage.about.button')}
                onClick={onAboutClick}
              />
            </AppFlexContainer>
          </AppFlexItem>
        </AppFlexContainer>
        <AppDivider size="large" />
        <UsersList />
        <AppDivider size="large" />
        <QuestionsList
          questionsData={topQuestions.data}
          title={t('homePage.topQuestions.header')}
          isLoading={topQuestions.isLoading}
          isFetching={topQuestions.isFetching}
          pageSize={READ_TOP_QUESTIONS_MAX}
        />
      </>
    )
  }, [
    stats.data,
    topQuestions.data,
    topQuestions.isLoading,
    topQuestions.isFetching,
    t,
    authUser,
    navigateTo,
    setBrowseQuestionsPageState,
  ])
}

export default HomePage
